import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  ShieldCheck, ShieldX, CheckCircle, XCircle, AlertCircle,
  FileText, User, Building2, Award, RefreshCw, ChevronDown,
  ChevronUp, ExternalLink, Loader2
} from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import api from '@/services/api'
import { toast } from 'react-toastify'

// ============================================================
// TIPOS
// ============================================================

interface ComplianceChecks {
  profile_name: boolean
  profile_phone: boolean
  profile_email: boolean
  profile_physical_address: boolean
  profile_attention_hours: boolean
  profile_complaints_contact: boolean
  doc_aviso_privacidad: boolean
  doc_terminos_condiciones: boolean
  doc_derechos_consumidor: boolean
  doc_info_nom247: boolean
  all_properties_with_construction_status: boolean
  subscription_active: boolean
  certificate_valid: boolean
}

interface ConstructionStatusDetail {
  total_public: number
  missing: number
  missing_titles: string[]
}

interface ComplianceResponse {
  success: boolean
  score: number
  seal_active: boolean
  checks: ComplianceChecks
  total_checks: number
  passed_checks: number
  construction_status_detail?: ConstructionStatusDetail
}

interface LegalDoc {
  doc_type: string
  is_published: boolean
  published_at?: string
  updated_at?: string
}

interface LegalDocsResponse {
  success: boolean
  docs: Record<string, LegalDoc>
}

type DocType = 'aviso_privacidad' | 'terminos_condiciones' | 'derechos_consumidor' | 'info_nom247'

// ============================================================
// CONSTANTES
// ============================================================

const CHECK_LABELS: Record<keyof ComplianceChecks, { label: string; description: string; category: string }> = {
  profile_name:                            { label: 'Nombre del agente',               description: 'Tu nombre completo en el perfil',                                          category: 'Perfil' },
  profile_phone:                           { label: 'Teléfono de contacto',             description: 'Número telefónico visible para clientes',                                  category: 'Perfil' },
  profile_email:                           { label: 'Correo electrónico',               description: 'Email de contacto visible para clientes',                                  category: 'Perfil' },
  profile_physical_address:                { label: 'Domicilio físico',                 description: 'Capturado y visible en tu sitio web',                                      category: 'Perfil' },
  profile_attention_hours:                 { label: 'Horario de atención',              description: 'Capturado y visible en tu sitio web',                                      category: 'Perfil' },
  profile_complaints_contact:              { label: 'Canal de atención a clientes',     description: 'Capturado y visible en tu sitio web',                                      category: 'Perfil' },
  doc_aviso_privacidad:                    { label: 'Aviso de Privacidad',              description: 'Publicado y visible en tu sitio web',                                      category: 'Documentos Legales' },
  doc_terminos_condiciones:                { label: 'Términos y Condiciones',           description: 'Publicados y visibles en tu sitio web',                                    category: 'Documentos Legales' },
  doc_derechos_consumidor:                 { label: 'Derechos del Consumidor',          description: 'Publicados conforme a la Ley Federal de Protección al Consumidor',         category: 'Documentos Legales' },
  doc_info_nom247:                         { label: 'Información NOM-247',              description: 'Sección informativa de cumplimiento normativo publicada',                  category: 'Documentos Legales' },
  all_properties_with_construction_status: { label: 'Propiedades con estatus constructivo', description: 'Cada propiedad pública activa debe tener estatus constructivo asignado', category: 'Propiedades' },
  subscription_active:                     { label: 'Suscripción activa',               description: 'Plan de InnovaAgent vigente',                                              category: 'Cuenta' },
  certificate_valid:                       { label: 'Certificación NOM-247 válida',     description: 'Certificación vigente emitida por organismo autorizado',                   category: 'Certificación' },
}

const DOC_LABELS: Record<DocType, { label: string; description: string }> = {
  aviso_privacidad:     { label: 'Aviso de Privacidad',    description: 'Generado con tus datos de contacto y domicilio fiscal' },
  terminos_condiciones: { label: 'Términos y Condiciones', description: 'Condiciones generales del servicio de intermediación inmobiliaria' },
  derechos_consumidor:  { label: 'Derechos del Consumidor', description: 'Conforme a la Ley Federal de Protección al Consumidor' },
  info_nom247:          { label: 'Información NOM-247',    description: 'Compromisos y datos de contacto conforme a la norma' },
}

const DOC_TYPES: DocType[] = ['aviso_privacidad', 'terminos_condiciones', 'derechos_consumidor', 'info_nom247']

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const NomCompliancePage: React.FC = () => {
  const [compliance, setCompliance]         = useState<ComplianceResponse | null>(null)
  const [docs, setDocs]                     = useState<Record<string, LegalDoc>>({})
  const [loading, setLoading]               = useState(true)
  const [refreshing, setRefreshing]         = useState(false)
  const [expandedDoc, setExpandedDoc]       = useState<DocType | null>(null)
  const [previewHtml, setPreviewHtml]       = useState<Record<DocType, string>>({
    aviso_privacidad: '', terminos_condiciones: '', derechos_consumidor: '', info_nom247: '',
  })
  const [loadingPreview, setLoadingPreview] = useState<DocType | null>(null)

  // ── Cargar compliance y estado de documentos ──
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    try {
      const [complianceRes, docsRes] = await Promise.all([
        api.get<ComplianceResponse>('/api/agents/nom/compliance'),
        api.get<LegalDocsResponse>('/api/agents/nom/legal-docs'),
      ])
      setCompliance(complianceRes.data)
      setDocs(docsRes.data.docs || {})
    } catch {
      toast.error('Error al cargar datos de cumplimiento NOM')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // ── Cargar preview del documento al expandir ──
  const handleExpandDoc = async (docType: DocType) => {
    if (expandedDoc === docType) {
      setExpandedDoc(null)
      return
    }
    setExpandedDoc(docType)
    if (previewHtml[docType]) return   // ya cargado, no volver a pedir
    setLoadingPreview(docType)
    try {
      const res = await api.get<{ success: boolean; html: string }>(
        `/api/agents/nom/legal-docs/preview/${docType}`
      )
      setPreviewHtml(prev => ({ ...prev, [docType]: res.data.html }))
    } catch {
      toast.error('Error al cargar la vista previa del documento')
    } finally {
      setLoadingPreview(null)
    }
  }

  // ── Helpers de color ──
  const getScoreColor = (score: number) =>
    score === 100 ? 'text-green-400' : score >= 70 ? 'text-yellow-400' : 'text-red-400'

  const getScoreBg = (score: number) =>
    score === 100 ? 'from-green-500/20 to-emerald-500/20 border-green-500/30'
    : score >= 70  ? 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30'
                   : 'from-red-500/20 to-rose-500/20 border-red-500/30'

  // ── Agrupar checks por categoría ──
  const groupedChecks = compliance
    ? Object.entries(compliance.checks).reduce((acc, [key, value]) => {
        const info = CHECK_LABELS[key as keyof ComplianceChecks]
        if (!info) return acc
        if (info.category === 'Documentos Legales') return acc
        if (!acc[info.category]) acc[info.category] = []
        acc[info.category].push({ key, value, ...info })
        return acc
      }, {} as Record<string, Array<{ key: string; value: boolean; label: string; description: string; category: string }>>)
    : {}

  // ── Loading ──
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-64">
          <div className="flex flex-col items-center space-y-4">
            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            <p className="text-white/60">Evaluando cumplimiento NOM-247...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <ShieldCheck className="w-7 h-7 text-blue-400" />
              Cumplimiento NOM-247-SE-2021
            </h1>
            <p className="text-white/60 mt-1 text-sm">
              Prácticas comerciales inmobiliarias · Exclusivo mercado México
            </p>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20 transition-all text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* ── Score Card ── */}
        {compliance && (
          <GlassCard className={`bg-gradient-to-r ${getScoreBg(compliance.score)} border`}>
            <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 flex-shrink-0">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
                    <circle
                      cx="50" cy="50" r="40" fill="none"
                      stroke={compliance.score === 100 ? '#22c55e' : compliance.score >= 70 ? '#eab308' : '#ef4444'}
                      strokeWidth="10"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - compliance.score / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xl font-bold ${getScoreColor(compliance.score)}`}>
                      {compliance.score}%
                    </span>
                  </div>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">
                    {compliance.score === 100 ? '¡Cumplimiento completo!' : 'Cumplimiento incompleto'}
                  </h2>
                  <p className="text-white/60 text-sm mt-1">
                    {compliance.passed_checks} de {compliance.total_checks} requisitos cumplidos
                  </p>
                  {compliance.score < 100 && (
                    <p className="text-white/50 text-xs mt-2">
                      Completa los requisitos faltantes para activar tu sello NOM-247
                    </p>
                  )}
                </div>
              </div>

              {/* Sello */}
              <div className="flex flex-col items-center gap-2">
                {compliance.seal_active ? (
                  <>
                    <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
                      <ShieldCheck className="w-10 h-10 text-green-400" />
                    </div>
                    <span className="text-green-400 text-sm font-medium">Sello Activo</span>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-white/5 border-2 border-white/20 flex items-center justify-center opacity-40">
                      <ShieldX className="w-10 h-10 text-white/40" />
                    </div>
                    <span className="text-white/40 text-sm">Sello inactivo</span>
                    <span className="text-white/30 text-xs text-center max-w-32">
                      Completa todos los requisitos
                    </span>
                  </>
                )}
              </div>
            </div>
          </GlassCard>
        )}

        {/* ── Checklist por categorías ── */}
        {Object.entries(groupedChecks).map(([category, items]) => (
          <GlassCard key={category} className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              {category === 'Perfil'             && <User      className="w-4 h-4 text-blue-400"   />}
              {category === 'Documentos Legales' && <FileText  className="w-4 h-4 text-purple-400" />}
              {category === 'Propiedades'        && <Building2 className="w-4 h-4 text-amber-400"  />}
              {category === 'Cuenta'             && <ShieldCheck className="w-4 h-4 text-green-400" />}
              {category === 'Certificación'      && <Award     className="w-4 h-4 text-yellow-400" />}
              {category}
            </h3>
            <div className="space-y-3">
              {items.map(({ key, value, label, description }) => (
                <div key={key} className={`flex items-start gap-3 p-3 rounded-xl ${value ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  {value
                    ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    : <XCircle    className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${value ? 'text-green-300' : 'text-red-300'}`}>{label}</p>
                    <p className="text-white/50 text-xs mt-0.5">{description}</p>

                    {!value && key === 'all_properties_with_construction_status' && (
                      <div className="mt-1 space-y-1">
                        <a href="/dashboard/properties" className="text-blue-400 text-xs hover:text-blue-300 inline-flex items-center gap-1">
                          Ir a Propiedades <ExternalLink className="w-3 h-3" />
                        </a>
                        {compliance?.construction_status_detail && compliance.construction_status_detail.missing > 0 && (
                          <div className="space-y-1">
                            <p className="text-amber-400/80 text-xs">
                              {compliance.construction_status_detail.missing} de {compliance.construction_status_detail.total_public} propiedades sin estatus:
                            </p>
                            <ul className="space-y-0.5">
                              {compliance.construction_status_detail.missing_titles.map((title, i) => (
                                <li key={i}>
                                  <a
                                    href={`/dashboard/properties?edit=${title}`}
                                    className="text-amber-400/70 text-xs hover:text-amber-300 flex items-center gap-1"
                                  >
                                    <span className="w-1 h-1 rounded-full bg-amber-400/50 flex-shrink-0" />
                                    {title.replace(/-/g, ' ')}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {!value && ['profile_phone','profile_email','profile_physical_address','profile_attention_hours','profile_complaints_contact'].includes(key) && (
                      <a href="/dashboard/profile" className="text-blue-400 text-xs hover:text-blue-300 mt-1 inline-flex items-center gap-1">
                        Completar en Mi Perfil <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    {!value && key === 'certificate_valid' && (
                      <p className="text-white/40 text-xs mt-1">
                        Contacta a InnovaAgent para iniciar el proceso de certificación
                      </p>
                    )}

                    {!value && key === 'subscription_active' && (
                      <a href="/dashboard/billing" className="text-blue-400 text-xs hover:text-blue-300 mt-1 inline-flex items-center gap-1">
                        Ver planes <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        ))}

        {/* ── Documentos Legales ── */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Documentos Legales
            </h3>
            <a             
              href="/dashboard/profile"
              className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              Gestionar en Mi Perfil <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="space-y-3">
            {DOC_TYPES.map((docType) => {
              const doc         = docs[docType]
              const isPublished = doc?.is_published || false
              const isExpanded  = expandedDoc === docType
              const isLoading   = loadingPreview === docType

              return (
                <div key={docType} className="border border-white/10 rounded-xl overflow-hidden">

                  {/* ── Fila principal ── */}
                  <button
                    onClick={() => handleExpandDoc(docType)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors"
                  >
                    {isPublished
                      ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      : <XCircle    className="w-4 h-4 text-red-400 flex-shrink-0"   />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{DOC_LABELS[docType].label}</p>
                      {!isPublished && (
                        <p className="text-red-400/70 text-xs mt-0.5 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          No visible en tu sitio — actívalo en Mi Perfil
                        </p>
                      )}
                    </div>
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full
                      ${isPublished ? 'bg-green-500/20 text-green-400' : 'bg-red-500/10 text-red-400/70'}`}>
                      {isPublished ? 'Visible' : 'Oculto'}
                    </span>
                    {isExpanded
                      ? <ChevronUp   className="w-4 h-4 text-white/40 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />
                    }
                  </button>

                  {/* ── Preview expandible ── */}
                  {isExpanded && (
                    <div className="border-t border-white/10 p-4">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8 gap-3 text-white/40">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">Cargando documento...</span>
                        </div>
                      ) : (
                        <div
                          className="text-white/70 text-sm leading-relaxed
                                     [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-base [&_h2]:mb-3 [&_h2]:mt-0
                                     [&_h3]:text-white/90 [&_h3]:font-semibold [&_h3]:text-sm [&_h3]:mt-4 [&_h3]:mb-2
                                     [&_p]:mb-3 [&_p]:text-white/65
                                     [&_ul]:mb-3 [&_ul]:pl-4 [&_li]:mb-1 [&_li]:text-white/65
                                     [&_strong]:text-white/90
                                     [&_.legal-meta]:text-white/35 [&_.legal-meta]:text-xs [&_.legal-meta]:italic"
                          dangerouslySetInnerHTML={{ __html: previewHtml[docType] }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </GlassCard>


        {/* ── Info NOM ── */}
        <GlassCard className="p-6 bg-blue-500/5 border border-blue-500/20">
          <div className="flex gap-4">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm text-white/70">
              <p className="font-medium text-white">¿Qué es la NOM-247-SE-2021?</p>
              <p>
                Norma Oficial Mexicana que establece los requisitos de información y prácticas
                comerciales para la compraventa o arrendamiento de inmuebles en México.
              </p>
              <p>
                Al completar todos los requisitos y obtener tu certificación, tu sitio mostrará
                automáticamente el sello de cumplimiento verificado.
              </p>
            </div>
          </div>
        </GlassCard>

      </motion.div>
    </DashboardLayout>
  )
}

export default NomCompliancePage
